import { anonymizeFaceCapture } from "./face-capture-anonymizer";
import {
  getKioskBindingSnapshot,
  type KioskBindingV1,
} from "../pages/kiosk/model/kiosk-binding";
import type {
  DetectResult,
  FaceBox,
} from "../views/mobile-app-checkout/useFaceGate";

const RELAY_TIMEOUT_MS = 12_000;

export type FaceCaptureRelayMetadata = {
  eventId: string;
  captureTime: string;
  kiosk: { id: string; name?: string };
  store: { id: string; name?: string };
  zone: { id: string; name?: string };
  detectionResult: "passed" | "no_face" | "too_small";
  faceBoxes: Array<Pick<FaceBox, "x" | "y" | "width" | "height">>;
  dimensions: { width: number; height: number };
  brightness: number;
  appVersion: string;
  placement?: KioskBindingV1["placement"];
  environment?: string;
};

export type FaceCaptureRelayInput = {
  eventId: string;
  storeKey: string;
  zoneId: string;
  result: DetectResult;
};

export class FaceCaptureRelayError extends Error {
  readonly name = "FaceCaptureRelayError";

  constructor(
    message: string,
    readonly status?: number,
    readonly stage: "anonymize" | "upload" = "upload",
  ) {
    super(message);
  }
}

export function buildFaceCaptureMetadata(
  input: FaceCaptureRelayInput,
  binding: KioskBindingV1 | null = getKioskBindingSnapshot(),
): FaceCaptureRelayMetadata | null {
  const capture = input.result.capture;
  if (!capture.imageDataUrl) return null;

  const appVersion = String(import.meta.env.VITE_APP_VERSION || "development");
  const kioskId = binding?.kioskInstallationId || "unknown-kiosk";
  const storeId = binding?.storeKey || input.storeKey;
  const zoneId = binding?.zoneId || input.zoneId;

  return {
    eventId: input.eventId,
    captureTime: new Date(capture.capturedAt ?? Date.now()).toISOString(),
    kiosk: { id: kioskId },
    store: {
      id: storeId,
      ...(binding?.storeName ? { name: binding.storeName } : {}),
    },
    zone: {
      id: zoneId,
      ...(binding?.zoneName ? { name: binding.zoneName } : {}),
    },
    detectionResult: input.result.passed
      ? "passed"
      : input.result.reason,
    faceBoxes: capture.boxes.map(({ x, y, width, height }) => ({
      x,
      y,
      width,
      height,
    })),
    dimensions: {
      width: capture.frameWidth,
      height: capture.frameHeight,
    },
    brightness: capture.brightness ?? 0,
    appVersion,
    ...(binding
      ? {
          placement: binding.placement,
          environment: String(import.meta.env.VITE_APP_ENV || "development"),
        }
      : {}),
  };
}

export async function submitFaceCapture(
  input: FaceCaptureRelayInput,
): Promise<{ accepted: boolean; disabled?: boolean }> {
  const relayUrl = String(import.meta.env.VITE_FACE_CAPTURE_RELAY_URL || "")
    .trim()
    .replace(/\/$/, "");
  const relayToken = String(import.meta.env.VITE_FACE_CAPTURE_RELAY_TOKEN || "").trim();

  if (!relayUrl || !relayToken) {
    return { accepted: false, disabled: true };
  }

  const metadata = buildFaceCaptureMetadata(input);
  if (!metadata) return { accepted: false };

  // Faces are blurred before the frame leaves the kiosk. If blurring fails the
  // frame is dropped: an unblurred image is never uploaded.
  let image: Blob;
  try {
    image = await anonymizeFaceCapture(
      dataUrlToBlob(input.result.capture.imageDataUrl!),
      metadata.faceBoxes,
      metadata.dimensions,
    );
  } catch (error) {
    if (error instanceof FaceCaptureRelayError) throw error;
    throw new FaceCaptureRelayError(
      "Face capture anonymization failed.",
      undefined,
      "anonymize",
    );
  }

  const body = new FormData();
  body.append("image", image, "face-capture.jpg");
  body.append("metadata", JSON.stringify(metadata));

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), RELAY_TIMEOUT_MS);

  try {
    const response = await fetch(`${relayUrl}/v1/face-captures`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${relayToken}`,
      },
      body,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new FaceCaptureRelayError(
        `Face capture relay returned HTTP ${response.status}.`,
        response.status,
      );
    }

    return { accepted: true };
  } catch (error) {
    if (error instanceof FaceCaptureRelayError) throw error;
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new FaceCaptureRelayError("Face capture relay timed out.");
    }
    throw new FaceCaptureRelayError(
      error instanceof Error
        ? error.message
        : "Face capture relay request failed.",
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function dataUrlToBlob(dataUrl: string): Blob {
  const separatorIndex = dataUrl.indexOf(",");
  if (separatorIndex < 0) {
    throw new FaceCaptureRelayError("Captured frame is not a valid data URL.");
  }

  const header = dataUrl.slice(0, separatorIndex);
  const encoded = dataUrl.slice(separatorIndex + 1);
  const mimeType = header.match(/^data:([^;]+);base64$/)?.[1] || "image/jpeg";
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
}
